/* eslint-disable no-nested-ternary */
import React, { useContext, useState, useEffect } from 'react';
import { GridApi, GridOptions, CellClickedEvent } from 'ag-grid-community';
import { useTheme } from '@material-ui/core/styles';
import { AgGridTableWrapper } from '@mummssoftware/common-ui';
import { LicenseManager } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { ExpandedCarePlans } from './ExpandedCarePlans';
import {
  EffPlanTableCellRenderer,
  NoteCountCellRenderer,
} from './CellRenderer';
import { Context } from '../../context';
import { Resolved } from '../Context/constants';

import 'ag-grid-community/dist/styles/ag-grid.css';
import '@mummssoftware/common-ui/style/css/ag-theme-hummingbird.css';
import 'ag-grid-community/dist/styles/ag-theme-alpine.css';

LicenseManager &&
  LicenseManager.setLicenseKey(process.env.REACT_APP_AG_GRID_KEY as string);

type CarePlanProblemsTableProps = {
  viewModePrint?: boolean;
};

const frameworkComponents = {
  lastUpdatedPlanCellRenderer: EffPlanTableCellRenderer,
};

export const CarePlanTable = ({
  viewModePrint,
}: CarePlanProblemsTableProps) => {
  const {
    palette: { background, primary },
    props: { grid: gridTheme },
  } = useTheme() as any;

  const { patientCarePlanProblem, smallWidget } = useContext(Context);

  const solvedCPPPlans = patientCarePlanProblem.filter(
    (plan) => plan.resolveddate,
  );
  const editCPPPlans = patientCarePlanProblem.filter(
    (plan) => !plan.resolveddate,
  );

  // insert a full width row
  const resolvedCPPRow = {
    careplanproblemname: Resolved,
    fullWidth: true,
    id: 0,
  };

  // small view
  const sortedPatientCarePlanProblem = solvedCPPPlans.length
    ? [...editCPPPlans, resolvedCPPRow, ...solvedCPPPlans]
    : editCPPPlans;
  // extended view
  const patientCPPs = [...editCPPPlans, ...solvedCPPPlans];

  const [clearFocus, setClearFocus] = useState<boolean | null>(null);

  useEffect(() => {
    if (clearFocus && gridApi) {
      setTimeout(() => {
        gridApi.clearFocusedCell();
        setClearFocus(false);
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearFocus]);

  const editFormField = 'editform';
  const roleGroupName = 'roleGroupName';

  const columnTableDefs = [
    {
      headerName: '',
      field: roleGroupName,
      cellClass: 'no-border no-pad',
      suppressMenu: true,
      cellRendererFramework: NoteCountCellRenderer,
      width: 33,
    },
    {
      headerName: 'Choose Care Plan',
      field: 'careplanproblemname',
      cellClass: 'no-border',
      suppressMenu: true,
    },
    {
      headerName: 'Effective Date',
      field: 'modifieddate',
      cellClass: 'no-border',
      cellRendererFramework: EffPlanTableCellRenderer,
      suppressMenu: true,
      width: 175,
    },
  ];

  const [gridApi, setGridApi] = useState<GridApi | null>(null);

  const gridOptions: GridOptions = {
    defaultColDef: {
      resizable: true,
    },
    columnDefs: columnTableDefs,
    onCellClicked: (event: CellClickedEvent) => {
      setClearFocus(true);
    },
    onGridReady: (params: any) => {
      const gridAPI: GridApi = params.api;
      setGridApi(gridAPI);
      gridAPI.sizeColumnsToFit();
      // for PRINT to work!
      if (gridApi) {
        if (viewModePrint) {
          gridApi.setDomLayout('print');
        }
      }
    },
    isFullWidthCell(rowNode) {
      // in this example, we check the fullWidth attribute that we set
      // while creating the data. what check you do to decide if you
      // want a row full width is up to you, as long as you return a boolean
      // for this method.
      return rowNode.data.fullWidth;
    },
    fullWidthCellRenderer() {
      const message = 'Resolved Problems';

      const eDiv = document.createElement('div');
      eDiv.innerHTML = `<div style="display: flex;
                        justify-content: flex-start;
                        align-items: center;
                        font-weight: 600;
                        font-size: 14px;
                        font-family: 'Muli', sans-serif;
                        background-color: ${background.header};
                        color: ${primary.main};
                        border-top: 1px solid ${background.default};
                        border-bottom: 1px solid ${background.default};
                        padding-left: 50px;
                        height: 100%;">
                        ${message}</div>`;
      return eDiv.innerHTML;
    },
  };

  return (
    <>
      {smallWidget ? (
        <AgGridTableWrapper
          id="care-plan-table-wrapper"
          className={`${gridTheme.className} light hover`}
        >
          <AgGridReact
            gridOptions={gridOptions}
            rowData={sortedPatientCarePlanProblem || []}
            rowHeight={gridTheme.rowHeight}
            headerHeight={gridTheme.headerHeight}
            frameworkComponents={frameworkComponents}
          />
        </AgGridTableWrapper>
      ) : (
        <ExpandedCarePlans
          patientCPPs={patientCPPs as mumms.patientCarePlanProblem[]}
        />
      )}
    </>
  );
};

export default CarePlanTable;
